import {Box, Button, Field, Heading, Input, Show, Table, Text} from "@chakra-ui/react";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {dataApi, DocumentFile as DocFile} from "../../api/data-api";
import {DateTime} from "luxon";
import {useToast} from "@chakra-ui/toast";
import {openFile} from "../../components/hooks/FileLoader";
import {useRecoilState} from "recoil";
import {userData} from "../../components/hooks/recoilData";
import {errorNotify} from "../../components/hooks/CommonUtils";

export interface DocumentFileExt {
    id: number | null,
    name: string,
    file: File | null,
}

export const DocumentFile = (props: {
    documentId: number,
    editable: boolean
}) => {
    const [items, setItems] = useState<DocFile[]>([]);
    const toast = useToast();
    const [user] = useRecoilState(userData);
    const [showForm, setShowForm] = useState<boolean>(false);
    const [editable, setEditable] = useState<boolean>(false);
    const inputDefault: DocumentFileExt = useMemo(() => {
        return {
            id: null,
            name: "file",
            file: null,
        }
    }, []);
    const [inputParams, setInputParams] = useState<DocumentFileExt>(inputDefault);

    useEffect(() => {
        dataApi.documentFileList(props.documentId)
            .then(o => {
                if (o)
                    setItems(o);
            })
            .catch((err) => errorNotify(err, toast));

        dataApi.documentGet(props.documentId)
            .then(it => {
                setEditable(user?.ID === it.USER_ID && !it.READY && !it.SIGNED && !it.REJECTED);
            })
            .catch((err) => errorNotify(err, toast));
    }, [props, toast, user]);

    const fileDelete = useCallback((it: DocFile) => {
        if (!it)
            return;
        dataApi.documentFileDelete(it.ID)
            .then((o) => {
                if (o) {
                    toast({title: "Delete", status: "success"});
                    setItems(prev => {
                        return prev.filter(o => o.ID !== it.ID);
                    })
                } else {
                    toast({title: "Delete failed", status: "error"});
                }
            })
            .catch((err) => errorNotify(err, toast));
    }, [toast]);

    const prepareInput = useCallback((item: DocFile | null) => {
        setInputParams({
            id: item ? item.ID : null,
            name: item ? item.NAME : "file",
            file: null,
        });
    }, []);

    const createOrUpdate = useCallback(() => {
        if (!inputParams.name || !inputParams.file)
            return;
        dataApi.documentFileCreateOrUpdate(inputParams.id, props.documentId, inputParams.name, inputParams.file)
            .then((value) => {
                toast({title: "Save", status: "success"});
                setItems(prev => {
                    let findO = prev.find(o => o.ID === value.ID);
                    const lst = [...prev];
                    if (findO) {
                        lst[prev.indexOf(findO)] = value;
                    } else {
                        lst.push(value);
                    }
                    return lst;
                });
                if (!inputParams.id) {
                    setInputParams(prev => {
                        return {...prev, id: value.ID};
                    });
                }
            })
            .catch((err) => errorNotify(err, toast));
    }, [inputParams, props, toast]);

    const fileDownload = useCallback((it: DocFile) => {
        if (!it)
            return;
        dataApi.documentFileDataGet(it.ID, it.NAME)
            .catch((err) => errorNotify(err, toast));
    }, [toast]);

    return (
        <Box borderWidth={1} borderColor="gray">
            {/*<Stack width="full" gap="5">*/}
            <Show when={props.editable}>
                <Box>
                    <Button disabled={!editable}
                            onClick={e => {
                                if (showForm) {
                                    setShowForm(false);
                                } else {
                                    prepareInput(null);
                                    setShowForm(true);
                                }
                            }}>
                        Создать новый
                    </Button>
                </Box>
                <Show when={showForm}>
                    <Heading size="xl">Файл {inputParams.id}</Heading>
                    <Box borderWidth={1} borderColor="gray">
                        <Field.Root orientation="horizontal" required>
                            <Field.Label>Имя <Field.RequiredIndicator/></Field.Label>
                            <Input type="text" placeholder="Имя"
                                   value={String(inputParams.name)}
                                   onChange={(it) => setInputParams((prev) => {
                                       return {...prev, name: it.target.value}
                                   })}/>
                        </Field.Root>
                        <Field.Root orientation="horizontal" required>
                            <Field.Label>File loader <Field.RequiredIndicator/></Field.Label>
                            <Text>{inputParams.file?.name}</Text>
                            <Button borderRadius={0}
                                    onClick={e => {
                                        openFile(null, file => {
                                            setInputParams((prev) => {
                                                return {...prev, file: file, name: file.name};
                                            });
                                        })
                                    }}>
                                Load
                            </Button>
                        </Field.Root>
                        <Button borderRadius={0} variant="solid" colorScheme="teal" width="full"
                                disabled={!editable}
                                onClick={e => createOrUpdate()}>
                            {inputParams.id ? "Сохранить" : "Создать"}
                        </Button>
                    </Box>
                </Show>
            </Show>
            <Heading size="xl">Файлы</Heading>
            <Table.Root size="sm" variant="outline" striped>
                <Table.Header>
                    <Table.Row>
                        <Table.ColumnHeader>Id</Table.ColumnHeader>
                        <Table.ColumnHeader>Имя</Table.ColumnHeader>
                        <Table.ColumnHeader>Размер</Table.ColumnHeader>
                        <Table.ColumnHeader>Хеш</Table.ColumnHeader>
                        <Table.ColumnHeader>Создан</Table.ColumnHeader>
                        <Table.ColumnHeader>Изменен</Table.ColumnHeader>
                        <Table.ColumnHeader>Действия</Table.ColumnHeader>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {items.map((item) => (
                        <Table.Row key={item.ID}>
                            <Table.Cell>{item.ID}</Table.Cell>
                            <Table.Cell>{item.NAME}</Table.Cell>
                            <Table.Cell>{item.CONTENT_SIZE}</Table.Cell>
                            <Table.Cell>{item.CONTENT_HASH}</Table.Cell>
                            <Table.Cell>{item.CREATED ? DateTime.fromMillis(item.CREATED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.VERSION ? DateTime.fromMillis(item.VERSION).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>
                                <Button onClick={e => fileDownload(item)}>
                                    Скачать
                                </Button>
                                <Show when={props.editable}>
                                    <Show when={editable}>
                                        <Button onClick={e => {
                                            prepareInput(item);
                                            setShowForm(true);
                                        }}>Редактировать</Button>
                                        <Button onClick={e => fileDelete(item)}>Удалить</Button>
                                    </Show>
                                </Show>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table.Root>
            {/*</Stack>*/}
        </Box>
    );
};